import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, ArrowLeft, Skull, Heart, Sword, Loader2 } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import DualMediaPanel from "@/components/game/DualMediaPanel";
import NarrativeBlock from "@/components/game/NarrativeBlock";

/**
 * Multiplayer Phase 1: Live Spectator.
 *
 * Two modes:
 *  - No :runId → show a grid of currently-live runs to browse
 *  - :runId set → poll /api/spectate/:runId every 4s and render read-only game view
 */
export default function SpectatePage() {
  const { runId } = useParams();
  return runId ? <SpectateRun runId={runId} /> : <SpectateBrowse />;
}

// ---------- BROWSE ----------
function SpectateBrowse() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/spectate/live");
      setRuns(res.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // gentle refresh — new runs pop in
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="spectate-browse">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/")}
            className="btn-glow flex items-center gap-2 text-xs tracking-[0.3em]"
            data-testid="spectate-back-btn"
          >
            <ArrowLeft size={14} /> BACK
          </button>
          <div className="text-center">
            <div className="text-xs tracking-[0.5em] blood-text flex items-center justify-center gap-2">
              <Eye size={14} /> LIVE HEROES <Eye size={14} />
            </div>
            <h1 className="font-heading text-3xl md:text-5xl gold-text mt-1">The Descent Watches</h1>
            <p className="ash-text text-sm mt-2 italic">Choose a champion to witness. Their fate is written turn by turn.</p>
          </div>
          <div className="w-20" />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin ash-text" size={32} />
          </div>
        ) : !runs?.length ? (
          <div className="panel p-12 text-center" data-testid="spectate-empty">
            <Skull className="mx-auto ash-text mb-4" size={48} />
            <p className="font-heading text-2xl gold-text">The dungeon is silent.</p>
            <p className="ash-text mt-2 text-sm">No heroes are currently descending. Check back in a few minutes.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="spectate-grid">
            {runs.map((r) => (
              <SpectateCard key={r.run_id} run={r} onClick={() => navigate(`/spectate/${r.run_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SpectateCard({ run, onClick }) {
  const hpPct = run.max_health ? Math.max(0, Math.min(100, Math.round((run.health / run.max_health) * 100))) : 0;
  const hpColor = hpPct > 60 ? "#5ec38a" : hpPct > 30 ? "#e6c07a" : "#d64b3b";
  const bg = run.scene_url || run.portrait_url;

  return (
    <button
      onClick={onClick}
      className="panel relative overflow-hidden text-left hover:scale-[1.02] transition-transform"
      style={{ minHeight: 220 }}
      data-testid={`spectate-card-${run.run_id}`}
    >
      {bg && (
        <img
          src={absUrl(bg)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

      <div className="relative p-4 h-full flex flex-col justify-end">
        <div className="flex items-center gap-2 mb-2">
          {run.in_combat && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.2em] blood-text bg-black/70 border border-[#d96534]/60 rounded">
              <Sword size={10} /> IN COMBAT
            </span>
          )}
          <span className="text-[9px] tracking-[0.3em] gold-text">
            F{run.floor}/{run.final_floor}
          </span>
          <span className="text-[9px] tracking-[0.3em] ash-text">LVL {run.level}</span>
        </div>
        <h3 className="font-heading text-xl leading-tight gold-text truncate">{run.player_name}</h3>
        <div className="text-xs ash-text mt-0.5">{run.class_name} · Turn {run.turn}</div>

        {/* HP bar */}
        <div className="mt-3 h-2 w-full bg-black/60 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${hpPct}%`, background: hpColor }}
          />
        </div>
        <div className="flex justify-between text-[9px] mt-1 ash-text">
          <span className="flex items-center gap-1"><Heart size={9} />{run.health}/{run.max_health}</span>
          <span>{run.chapter_title}</span>
        </div>
      </div>
    </button>
  );
}

// ---------- LIVE VIEW ----------
function SpectateRun({ runId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ended, setEnded] = useState(null);
  const [prevTurn, setPrevTurn] = useState(-1);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/spectate/${runId}`);
        if (cancelled) return;
        setData(res);
        setPrevTurn((prev) => {
          if (prev >= 0 && res.turn > prev) setFlash(true);
          setTimeout(() => setFlash(false), 900);
          return res.turn;
        });
      } catch (e) {
        if (e?.response?.status === 410) setEnded("fallen");
        else if (e?.response?.status === 404) setEnded("gone");
      }
    };

    poll();
    const t = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [runId]);

  if (ended) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="spectate-ended">
        <div className="panel p-10 text-center max-w-lg">
          <Skull className="mx-auto blood-text mb-4" size={48} />
          <h2 className="font-heading text-3xl gold-text">
            {ended === "fallen" ? "This hero has fallen." : "This run has ended."}
          </h2>
          <p className="ash-text mt-3 text-sm italic">
            {ended === "fallen"
              ? "Check the Hall of Ashes to see how far they descended."
              : "The champion has finished their descent."}
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <button onClick={() => navigate("/spectate")} className="btn-glow text-xs tracking-[0.3em]" data-testid="spectate-back-list">
              WATCH ANOTHER
            </button>
            <button onClick={() => navigate("/hall")} className="btn-primary text-xs tracking-[0.3em]" data-testid="spectate-hall-btn">
              HALL OF ASHES
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin ash-text" size={32} />
      </div>
    );
  }

  const hpPct = data.max_health ? Math.round((data.health / data.max_health) * 100) : 0;
  const enemyHpPct = data.enemy_max_hp ? Math.round(((data.enemy_hp || 0) / data.enemy_max_hp) * 100) : 0;

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="spectate-live">
      {/* Header bar */}
      <div className="max-w-6xl mx-auto flex items-center justify-between mb-4">
        <button
          onClick={() => navigate("/spectate")}
          className="btn-glow flex items-center gap-2 text-xs tracking-[0.3em]"
          data-testid="spectate-back-to-list"
        >
          <ArrowLeft size={14} /> WATCH LIST
        </button>
        <div className="text-center">
          <div className="text-xs tracking-[0.4em] blood-text flex items-center justify-center gap-2">
            <Eye size={14} /> SPECTATING · LIVE
          </div>
          <h2 className="font-heading text-2xl gold-text">{data.player_name}</h2>
          <div className="text-[10px] tracking-[0.3em] ash-text">
            {data.class_name} · LVL {data.level} · FLOOR {data.floor}/{data.final_floor}
          </div>
        </div>
        <div className="w-24 text-right">
          {flash && (
            <span className="text-[10px] tracking-[0.3em] gold-text animate-pulse">NEW TURN</span>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-4">
        {/* Media panel — reuses the game's dual media view */}
        <DualMediaPanel
          sceneUrl={data.scene_url}
          portraitUrl={data.portrait_url}
          className={data.class_name}
          loadingScene={false}
        />

        {/* Player HP + enemy HP */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="panel p-4">
            <div className="text-[10px] tracking-[0.3em] ash-text mb-1 flex items-center gap-1">
              <Heart size={11} /> HERO HEALTH
            </div>
            <div className="h-3 w-full bg-black/60 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-500"
                   style={{ width: `${hpPct}%`, background: hpPct > 60 ? "#5ec38a" : hpPct > 30 ? "#e6c07a" : "#d64b3b" }} />
            </div>
            <div className="text-xs mt-1 gold-text">{data.health}/{data.max_health}</div>
          </div>
          {data.enemy_name ? (
            <div className="panel p-4" data-testid="spectate-enemy-panel">
              <div className="text-[10px] tracking-[0.3em] blood-text mb-1 flex items-center gap-1">
                <Sword size={11} /> {data.is_boss_room ? "BOSS · " : ""}{data.enemy_name}
              </div>
              <div className="h-3 w-full bg-black/60 rounded-full overflow-hidden">
                <div className="h-full transition-all duration-500"
                     style={{ width: `${enemyHpPct}%`, background: "#d64b3b" }} />
              </div>
              <div className="text-xs mt-1 gold-text">{data.enemy_hp}/{data.enemy_max_hp}</div>
            </div>
          ) : (
            <div className="panel p-4 opacity-70">
              <div className="text-[10px] tracking-[0.3em] ash-text mb-1">CHAPTER</div>
              <div className="font-heading text-lg gold-text truncate">{data.chapter_title || "The Descent"}</div>
              <div className="text-xs ash-text mt-1">Turn {data.turn}</div>
            </div>
          )}
        </div>

        {/* Narrative */}
        <div className="panel p-6">
          <div className="text-[10px] tracking-[0.4em] ash-text mb-3">CURRENT BEAT</div>
          <NarrativeBlock text={data.current_narrative || ""} />
        </div>

        {/* Recent history */}
        {data.history?.length > 0 && (
          <div className="panel p-6">
            <div className="text-[10px] tracking-[0.4em] ash-text mb-3">RECENT TURNS</div>
            <ol className="space-y-3">
              {data.history.slice().reverse().map((h, i) => (
                <li key={i} className="text-xs ash-text italic border-l-2 border-[#d69e47]/30 pl-3">
                  {h.text?.slice(0, 220)}{h.text?.length > 220 ? "…" : ""}
                </li>
              ))}
            </ol>
          </div>
        )}

        <p className="text-center text-[10px] tracking-[0.3em] ash-text py-4">
          POLLING EVERY 4 SECONDS · READ-ONLY
        </p>
      </div>
    </div>
  );
}
